/**
 * Pure helpers for the screen-print SPEC editor and the press carousel
 * (docs/screen-print-station-audit-and-plan-v1.md § 3.1, § 3.6). No DOM, no fetch — everything here is
 * unit-tested against fixtures, including the one rule the drawing must never get wrong: which station
 * a screen sits on, and where that station is on the ring.
 */
import type { components } from '@/lib/api/types.gen';

export type PressSummary = components['schemas']['PressSummary'];
export type DesignScreenPrintSpec = NonNullable<
  components['schemas']['DesignSummary']['screenPrintSpec']
>;
/** The wire shape `PATCH /designs/:id` accepts — the spec without the server's `setAt` / `setBy`. */
export type DesignScreenPrintSpecBox = NonNullable<
  components['schemas']['DesignUpdateRequest']['screenPrintSpec']
>;
export type ScreenSpec = DesignScreenPrintSpecBox['screens'][number];

/** The house values offered as chips (D6 — unconfirmed by the owner; "other" always allowed). */
export const MESH_CHOICES = [110, 156, 200, 230, 305] as const;
export const FRAME_CHOICES = ['20 × 24', '23 × 31'] as const;
/** Mirrors `PRESS_MAX_STATIONS` in contracts — the longest carousel the model allows for. */
export const PRESS_MAX_STATIONS = 20;

export const HEX_RE = /^#[0-9a-fA-F]{6}$/;
export function isValidHex(v: string): boolean {
  return HEX_RE.test(v);
}

/**
 * One editable row. Strings throughout so a half-typed value never fights the input; `key` is a stable
 * React identity that survives reordering (the print order is the ROW ORDER, numbered at save).
 */
export type ScreenDraft = {
  key: string;
  name: string;
  pantone: string;
  hex: string;
  ink: string;
  meshCount: string;
  frame: string;
  flashAfter: boolean;
  head: string;
};

let draftSeq = 0;
export function newDraft(partial: Partial<Omit<ScreenDraft, 'key'>> = {}): ScreenDraft {
  draftSeq += 1;
  return {
    key: `draft-${draftSeq}`,
    name: '',
    pantone: '',
    hex: '',
    ink: '',
    meshCount: '',
    frame: '',
    flashAfter: false,
    head: '',
    ...partial,
  };
}

/** Rows from a stored spec, in print order. An absent spec yields one blank row to start typing into. */
export function draftsFromSpec(spec: DesignScreenPrintSpec | undefined): ScreenDraft[] {
  if (!spec) return [newDraft()];
  return [...spec.screens]
    .sort((a, b) => a.seq - b.seq)
    .map((s) =>
      newDraft({
        name: s.name,
        pantone: s.pantone ?? '',
        hex: s.hex ?? '',
        ink: s.ink ?? '',
        meshCount: s.meshCount != null ? String(s.meshCount) : '',
        frame: s.frame ?? '',
        flashAfter: s.flashAfter ?? false,
        head: s.head != null ? String(s.head) : '',
      }),
    );
}

/** What stops a save, in the operator's words. Empty = saveable. */
export function draftErrors(drafts: readonly ScreenDraft[], stations: number): string[] {
  const errors: string[] = [];
  if (drafts.length === 0) errors.push('Add at least one colour.');
  drafts.forEach((d, i) => {
    const label = d.name.trim() || `colour ${i + 1}`;
    if (!d.name.trim()) errors.push(`Colour ${i + 1} needs a name.`);
    if (d.hex && !isValidHex(d.hex)) errors.push(`${label}: hex must look like #1E4FD8.`);
    if (d.meshCount) {
      const m = Number(d.meshCount);
      if (!Number.isInteger(m) || m < 20 || m > 500)
        errors.push(`${label}: mesh count must be a whole number between 20 and 500.`);
    }
    if (d.head) {
      const h = Number(d.head);
      if (!Number.isInteger(h) || h < 1 || h > stations)
        errors.push(`${label}: head must be a station from 1 to ${stations}.`);
    }
  });
  return errors;
}

/**
 * The spec box to PATCH: rows become screens numbered by ROW ORDER (1-based), blanks dropped, the
 * server's `setAt` / `setBy` never sent. `null` when nothing is saveable so the caller can refuse.
 */
export function specBoxFromDrafts(
  drafts: readonly ScreenDraft[],
  extras: { cureTempF: string; cureSeconds: string; pressNotes: string; pressId?: string },
  stations: number,
): DesignScreenPrintSpecBox | null {
  if (draftErrors(drafts, stations).length > 0) return null;
  const screens: ScreenSpec[] = drafts.map((d, i) => ({
    seq: i + 1,
    name: d.name.trim(),
    ...(d.pantone.trim() ? { pantone: d.pantone.trim() } : {}),
    ...(d.hex ? { hex: d.hex.toUpperCase() } : {}),
    ...(d.ink.trim() ? { ink: d.ink.trim() } : {}),
    ...(d.meshCount ? { meshCount: Number(d.meshCount) } : {}),
    ...(d.frame.trim() ? { frame: d.frame.trim() } : {}),
    ...(d.flashAfter ? { flashAfter: true } : {}),
    ...(d.head ? { head: Number(d.head) } : {}),
  }));
  const cureTempF = extras.cureTempF ? Number(extras.cureTempF) : undefined;
  const cureSeconds = extras.cureSeconds ? Number(extras.cureSeconds) : undefined;
  return {
    screens,
    ...(cureTempF != null && Number.isFinite(cureTempF) ? { cureTempF } : {}),
    ...(cureSeconds != null && Number.isFinite(cureSeconds) ? { cureSeconds } : {}),
    ...(extras.pressNotes.trim() ? { pressNotes: extras.pressNotes.trim() } : {}),
    ...(extras.pressId ? { pressId: extras.pressId } : {}),
  };
}

/** "3 colours" / "1 colour" for the library row; undefined when no spec (the row shows nothing). */
export function screenCountLabel(spec: DesignScreenPrintSpec | undefined): string | undefined {
  if (!spec) return undefined;
  const n = spec.screens.length;
  return `${n} colour${n === 1 ? '' : 's'}`;
}

// ── The carousel ────────────────────────────────────────────────────────────────────────────────

/** The subset of a press the ring needs — so a form's unsaved values can be drawn before they exist. */
export type PressShape = Pick<PressSummary, 'stations' | 'printHeads' | 'flashStations' | 'kind'>;

/** A screen as the ring sees it — whatever it is drawn from (saved spec or live drafts). */
export type RingScreen = { label: string; head?: number; hex?: string; meshCount?: number };

export type StationTile = {
  station: number;
  /** Degrees clockwise from 12 o'clock. Station 1 sits at the bottom (180°), as Press Fit draws it. */
  angleDeg: number;
  kind: 'head' | 'flash' | 'empty' | 'conflict';
  screen?: RingScreen;
};

export type StationLayout = {
  tiles: StationTile[];
  /** Screens with no head yet — listed after the ring, never lost. */
  unassigned: RingScreen[];
  /** Amber, never a refusal (§ 3.6): the press is the owner's; the drawing only says what it sees. */
  warnings: string[];
};

/** Station k of n sits at 180° − (k−1)·(360/n): 1 at the bottom, numbering the way Press Fit runs. */
export function stationAngle(station: number, stations: number): number {
  return 180 - (station - 1) * (360 / stations);
}

export function ringScreensFromDrafts(drafts: readonly ScreenDraft[]): RingScreen[] {
  return drafts.map((d, i) => ({
    label: d.name.trim() || `colour ${i + 1}`,
    ...(d.head && Number.isInteger(Number(d.head)) ? { head: Number(d.head) } : {}),
    ...(d.hex && isValidHex(d.hex) ? { hex: d.hex } : {}),
    ...(d.meshCount && Number.isInteger(Number(d.meshCount))
      ? { meshCount: Number(d.meshCount) }
      : {}),
  }));
}

export function ringScreensFromSpec(spec: DesignScreenPrintSpec): RingScreen[] {
  return [...spec.screens]
    .sort((a, b) => a.seq - b.seq)
    .map((s) => ({
      label: s.name,
      ...(s.head != null ? { head: s.head } : {}),
      ...(s.hex ? { hex: s.hex } : {}),
      ...(s.meshCount != null ? { meshCount: s.meshCount } : {}),
    }));
}

/**
 * Lay the screens onto the press. Every station gets exactly one tile; a screen whose head is a flash
 * station or is shared with another screen becomes a `conflict` tile and a warning; a screen with no
 * head goes to `unassigned`. More screens than print heads is a warning too — counted honestly, not
 * refused, because the press is the owner's.
 */
export function stationLayout(screens: readonly RingScreen[], press: PressShape): StationLayout {
  const flashes = new Set(press.flashStations);
  const byHead = new Map<number, RingScreen[]>();
  const unassigned: RingScreen[] = [];
  for (const s of screens) {
    if (s.head == null || s.head < 1 || s.head > press.stations) unassigned.push(s);
    else byHead.set(s.head, [...(byHead.get(s.head) ?? []), s]);
  }
  const warnings: string[] = [];
  if (screens.length > press.printHeads) {
    warnings.push(`${screens.length} screens, ${press.printHeads} print heads on this press.`);
  }
  const tiles: StationTile[] = [];
  for (let k = 1; k <= press.stations; k += 1) {
    const here = byHead.get(k) ?? [];
    const angleDeg = stationAngle(k, press.stations);
    if (here.length > 1) {
      warnings.push(
        `Head ${k} has ${here.length} screens: ${here.map((s) => s.label).join(', ')}.`,
      );
      tiles.push({ station: k, angleDeg, kind: 'conflict', screen: here[0] });
    } else if (here.length === 1 && flashes.has(k)) {
      warnings.push(`Head ${k} is a flash station — ${here[0]?.label} cannot print there.`);
      tiles.push({ station: k, angleDeg, kind: 'conflict', screen: here[0] });
    } else if (here.length === 1) {
      tiles.push({ station: k, angleDeg, kind: 'head', screen: here[0] });
    } else if (flashes.has(k)) {
      tiles.push({ station: k, angleDeg, kind: 'flash' });
    } else {
      tiles.push({ station: k, angleDeg, kind: 'empty' });
    }
  }
  if (unassigned.length > 0 && press.kind === 'automatic') {
    warnings.push(
      `${unassigned.length} screen${unassigned.length === 1 ? '' : 's'} with no head yet — head order is print order on an automatic.`,
    );
  }
  return { tiles, unassigned, warnings };
}

// ── Press form (Settings → Presses) ───────────────────────────────────────────────────────────────

/** "2, 8" → [2, 8]; anything that is not a whole number is refused so a typo never becomes a station. */
export function parseFlashStations(raw: string): number[] | null {
  const parts = raw
    .split(/[,\s]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const out: number[] = [];
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null;
    out.push(Number(p));
  }
  return out;
}

/** The server's physical refinement, mirrored so the Save button disables before a 400. */
export function pressFormValid(form: {
  name: string;
  stations: number;
  printHeads: number;
  flashStations: number[] | null;
}): boolean {
  if (!form.name.trim()) return false;
  if (!Number.isInteger(form.stations) || form.stations < 1 || form.stations > PRESS_MAX_STATIONS)
    return false;
  if (!Number.isInteger(form.printHeads) || form.printHeads < 1) return false;
  if (form.flashStations === null || form.flashStations.length > 4) return false;
  if (form.printHeads + form.flashStations.length > form.stations) return false;
  if (new Set(form.flashStations).size !== form.flashStations.length) return false;
  return form.flashStations.every((s) => s >= 1 && s <= form.stations);
}

/** "12 stations · 10 heads · flash at 2, 8" for a table row. */
export function pressShapeLabel(p: PressShape): string {
  const flash =
    p.flashStations.length > 0 ? ` · flash at ${p.flashStations.join(', ')}` : ' · no flash';
  return `${p.stations} stations · ${p.printHeads} heads${flash}`;
}
